import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useEffect } from 'react';

// Layout
import { Layout } from '@/components/layout/Layout';
import { ProtectedRoute, PublicRoute } from '@/components/layout/ProtectedRoute';

// Pages
import { Home } from '@/pages/Home';
import { Login } from '@/pages/Login';
import { Register } from '@/pages/Register';
import { PageList } from '@/pages/PageList';
import { PageDetail } from '@/pages/PageDetail';
import { PageEditor } from '@/pages/PageEditor';
import { Search } from '@/pages/Search';
import { Profile } from '@/pages/Profile';
import { Admin } from '@/pages/Admin';
import { PageHistory } from '@/pages/PageHistory';
import { ApiKeys } from '@/pages/ApiKeys';
import { ForgotPassword } from '@/pages/ForgotPassword';
import { ResetPassword } from '@/pages/ResetPassword';
import { VerifyEmail } from '@/pages/VerifyEmail';
import { AdminSettings } from '@/pages/AdminSettings';
import { Favorites } from '@/pages/Favorites';
import { Spaces } from '@/pages/Spaces';
import { SpaceDetail } from '@/pages/SpaceDetail';
import { Subscriptions } from '@/pages/Subscriptions';
import { Notifications } from '@/pages/Notifications';
import { OAuthCallback } from '@/pages/OAuthCallback';
import { TwoFactorVerify } from '@/pages/TwoFactorVerify';
import { TwoFactorBackup } from '@/pages/TwoFactorBackup';
import { ApiDocs } from '@/pages/ApiDocs';
import { SharedPage } from '@/pages/SharedPage';

// Store
import { useThemeStore } from '@/stores';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const { theme, setTheme } = useThemeStore();

  // Initialize theme
  useEffect(() => {
    const stored = localStorage.getItem('theme-storage');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.state?.theme) {
          setTheme(parsed.state.theme);
        }
      } catch {
        // Use system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setTheme(prefersDark ? 'dark' : 'light');
      }
    } else {
      // Use system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setTheme(prefersDark ? 'dark' : 'light');
    }
  }, [setTheme]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          {/* Shared page - no layout */}
          <Route path="/s/:token" element={<SharedPage />} />

          <Route element={<Layout />}>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/pages" element={<PageList />} />
            <Route path="/pages/:slug" element={<PageDetail />} />
            <Route path="/search" element={<Search />} />
            <Route path="/api-docs" element={<ApiDocs />} />

            {/* Auth routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/verify-email"
              element={
                <PublicRoute>
                  <VerifyEmail />
                </PublicRoute>
              }
            />

            {/* OAuth callback */}
            <Route path="/auth/callback" element={<OAuthCallback />} />

            {/* Two-factor authentication routes */}
            <Route
              path="/2fa/verify"
              element={
                <PublicRoute>
                  <TwoFactorVerify />
                </PublicRoute>
              }
            />
            <Route
              path="/2fa/backup"
              element={
                <PublicRoute>
                  <TwoFactorBackup />
                </PublicRoute>
              }
            />

            {/* Protected routes - Editor+ */}
            <Route
              path="/pages/new"
              element={
                <ProtectedRoute requiredRole="editor">
                  <PageEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pages/:slug/edit"
              element={
                <ProtectedRoute requiredRole="editor">
                  <PageEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pages/:slug/history"
              element={
                <ProtectedRoute>
                  <PageHistory />
                </ProtectedRoute>
              }
            />

            {/* Protected routes - Any authenticated user */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/api-keys"
              element={
                <ProtectedRoute>
                  <ApiKeys />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/favorites"
              element={
                <ProtectedRoute>
                  <Favorites />
                </ProtectedRoute>
              }
            />

            {/* Spaces routes */}
            <Route
              path="/spaces"
              element={
                <ProtectedRoute>
                  <Spaces />
                </ProtectedRoute>
              }
            />
            <Route
              path="/spaces/:slug"
              element={
                <ProtectedRoute>
                  <SpaceDetail />
                </ProtectedRoute>
              }
            />

            {/* Subscriptions */}
            <Route
              path="/profile/subscriptions"
              element={
                <ProtectedRoute>
                  <Subscriptions />
                </ProtectedRoute>
              }
            />

            {/* Notifications */}
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />

            {/* Protected routes - Admin only */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRole="admin">
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminSettings />
                </ProtectedRoute>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: theme === 'dark' ? '#1f2937' : '#fff',
            color: theme === 'dark' ? '#fff' : '#1f2937',
          },
        }}
      />
    </QueryClientProvider>
  );
}

export default App;
